import React, { useState, useEffect } from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { apiService } from '@/services/api';
import { Building2, Loader2, AlertCircle } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

interface Client {
  id: number;
  legal_name: string;
  trade_name: string;
}

interface ClientSelectDropdownProps {
  value?: number;
  onValueChange: (clientId: number | undefined) => void;
  placeholder?: string;
  hideLabel?: boolean;
  showAllOption?: boolean;
}

const ClientSelectDropdown: React.FC<ClientSelectDropdownProps> = ({
  value,
  onValueChange,
  placeholder = 'Selecione um cliente',
  hideLabel = false,
  showAllOption = true,
}) => {
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    loadClients();
  }, []);

  const loadClients = async () => {
    try {
      setLoading(true);
      setError(null);
      console.log('🔍 [ClientSelectDropdown] Iniciando carregamento de clientes...');
      
      const clientsData = await apiService.getClients();
      console.log('✅ [ClientSelectDropdown] Clientes carregados:', clientsData?.length || 0);
      
      if (!Array.isArray(clientsData)) {
        throw new Error('Resposta inválida da API: esperado array de clientes');
      }
      
      setClients(clientsData);
      
      if (clientsData.length === 0) {
        console.warn('⚠️ [ClientSelectDropdown] Nenhum cliente encontrado');
        setError('Nenhum cliente disponível');
      }
    } catch (error: any) {
      console.error('❌ [ClientSelectDropdown] Erro ao carregar clientes:', error);
      const errorMessage = error?.message || 'Erro ao carregar clientes';
      setError(errorMessage);
      setClients([]);
      
      toast({
        title: 'Erro ao carregar clientes',
        description: errorMessage,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (clientIdStr: string) => {
    if (clientIdStr === 'all' || clientIdStr === '') {
      onValueChange(undefined);
    } else {
      onValueChange(Number(clientIdStr));
    }
  };

  return (
    <div className="space-y-2">
      {!hideLabel && (
        <Label className="flex items-center gap-2">
          <Building2 className="h-4 w-4 text-gray-500" />
          Cliente
        </Label>
      )}
      <Select
        value={value?.toString() || (showAllOption ? 'all' : undefined)}
        onValueChange={handleChange}
        disabled={loading || !!error}
      >
        <SelectTrigger className="w-full">
          {loading ? (
            <div className="flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" />
              <span>Carregando...</span>
            </div>
          ) : error ? (
            <div className="flex items-center gap-2 text-red-600">
              <AlertCircle className="h-4 w-4" />
              <span>Erro ao carregar</span>
            </div>
          ) : (
            <SelectValue placeholder={placeholder} />
          )}
        </SelectTrigger>
        <SelectContent>
          {showAllOption && <SelectItem value="all">Todos os clientes</SelectItem>}
          {clients.map((client) => (
            <SelectItem key={client.id} value={client.id.toString()}>
              <div className="flex flex-col">
                <span className="font-medium">{client.trade_name || client.legal_name}</span>
                {client.trade_name && client.legal_name && (
                  <span className="text-xs text-gray-500">{client.legal_name}</span>
                )}
              </div>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {error && !loading && (
        <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
          <AlertCircle className="h-3 w-3" />
          {error}
        </p>
      )}
    </div>
  );
};

export default ClientSelectDropdown;

